<template>
  <div class="home-training-list">
    <div class="year-tabs">
      <div
        class="tab-item"
        v-for="year in yearList"
        :key="year"
        :class="{ active: selectedYear === year }"
        @click="handleYearClick(year)"
      >
        {{ year }}
      </div>
    </div>

    <div
      v-for="section in sectionList"
      :key="section.majorType"
      class="training-section"
    >
      <div class="section-header">
        <span class="icon-square" :style="{ background: section.color }"></span>
        <span class="title">{{ section.title }}</span>
      </div>

      <el-table
        :data="getTrainingRows(section.majorType)"
        style="width: 100%"
        :header-cell-style="{ background: '#F4F6FA', color: '#8F939D', fontWeight: '500' }"
        :empty-text="loading ? '加载中...' : '暂无数据'"
      >
        <el-table-column prop="name" label="培训班名称" min-width="280" show-overflow-tooltip></el-table-column>
        <el-table-column label="年份" width="110" align="center">
          <template slot-scope="scope">
            {{ getRowYear(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column label="报名时间" min-width="260" align="center">
          <template slot-scope="scope">
            {{ formatDateRange(scope.row.enrollStartTime, scope.row.enrollEndTime) }}
          </template>
        </el-table-column>
        <el-table-column label="报名状态" width="140" align="center">
          <template slot-scope="scope">
            <span :class="['status-tag', getStatusClass(scope.row)]">{{
              getAvailabilityStatusText(scope.row)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="getOperationState(scope.row) === 'enrolled'"
              size="mini"
              class="enrolled-btn"
              disabled
            >已报名</el-button>
            <el-button
              v-else-if="getOperationState(scope.row) === 'auditing'"
              size="mini"
              class="auditing-btn"
              disabled
            >审核中</el-button>
            <el-button
              v-else-if="getOperationState(scope.row) === 'wait_pay' && canShowOperationBtn(scope.row)"
              type="primary"
              size="mini"
              class="wait-pay-btn"
              @click="handleEnroll(scope.row, section.majorType)"
            >待缴费</el-button>
            <el-button
              v-else-if="getOperationState(scope.row) === 'reapply' && canShowOperationBtn(scope.row)"
              type="primary"
              size="mini"
              class="enroll-btn"
              @click="handleEnroll(scope.row, section.majorType)"
            >重新报名</el-button>
            <el-button
              v-else-if="canShowEnrollBtn(scope.row)"
              type="primary"
              size="mini"
              class="enroll-btn"
              @click="handleEnroll(scope.row, section.majorType)"
            >报名</el-button>
            <el-button
              plain
              size="mini"
              class="detail-btn"
              @click="handleDetail(scope.row, section.majorType)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { resolveEnrollOperationState } from "@/utils/enrollCheckFlow";

export default {
  name: "HomeTrainingList",
  props: {
    yearList: {
      type: Array,
      default: () => []
    },
    selectedYear: {
      type: Number,
      default: () => new Date().getFullYear()
    },
    trainingMap: {
      type: Object,
      default: () => ({
        0: [],
        1: [],
        2: [],
        3: []
      })
    },
    userInfo: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    sections: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      defaultSections: [
        { majorType: 0, title: "专科培训", color: "#3E74C8" },
        { majorType: 1, title: "专项培训", color: "#64C6B0" },
        { majorType: 2, title: "专题培训", color: "#D9B03F" },
        { majorType: 3, title: "师资培训", color: "#4D6FB2" }
      ]
    };
  },
  computed: {
    sectionList() {
      if (Array.isArray(this.sections) && this.sections.length > 0) {
        return this.sections;
      }
      return this.defaultSections;
    }
  },
  methods: {
    handleYearClick(year) {
      this.$emit("year-change", year);
    },
    getTrainingRows(majorType) {
      const list = this.trainingMap[majorType];
      return Array.isArray(list) ? list : [];
    },
    parseDateTime(value) {
      if (!value) {
        return null;
      }
      const normalized = String(value).replace(/-/g, "/");
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      return date;
    },
    getRowYear(row) {
      const date = this.parseDateTime(row.enrollStartTime || row.examStartTime);
      return date ? date.getFullYear() : "-";
    },
    formatDate(value) {
      const date = this.parseDateTime(value);
      if (!date) {
        return "-";
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDateRange(start, end) {
      if (!start && !end) {
        return "-";
      }
      return `${this.formatDate(start)} ~ ${this.formatDate(end)}`;
    },
    getOperationState(row) {
      return resolveEnrollOperationState(row, this.userInfo);
    },
    getAvailabilityStatusText(row) {
      const now = Date.now();
      const start = this.parseDateTime(row.enrollStartTime);
      const end = this.parseDateTime(row.enrollEndTime);
      if (!start || !end) {
        return "未知";
      }
      if (now < start.getTime()) {
        return "未开始";
      }
      if (now > end.getTime()) {
        return "已结束";
      }
      return "报名中";
    },
    canShowEnrollBtn(row) {
      const state = this.getOperationState(row);
      if (state !== "enroll_apply" && state !== "enroll_pay") {
        return false;
      }
      return this.getAvailabilityStatusText(row) === "报名中";
    },
    canShowOperationBtn(row) {
      return this.getAvailabilityStatusText(row) === "报名中";
    },
    getStatusClass(row) {
      const status = this.getAvailabilityStatusText(row);
      if (status === "报名中") {
        return "status-open";
      }
      if (status === "未开始") {
        return "status-not-start";
      }
      if (status === "已结束") {
        return "status-finished";
      }
      return "status-unknown";
    },
    handleEnroll(row, majorType) {
      this.$emit("enroll", { ...row, majorType });
    },
    handleDetail(row, majorType) {
      this.$emit("detail", { ...row, majorType });
    }
  }
};
</script>

<style scoped lang="scss">
.home-training-list {
  margin-top: 20px;
  background: transparent;
  padding-bottom: 10px;

  .year-tabs {
    display: flex;
    margin-bottom: 0;
    border-bottom: 3px solid #2f6ec6;

    .tab-item {
      width: 120px;
      height: 60px;
      line-height: 60px;
      text-align: center;
      background: #9198a1;
      color: #fff;
      font-size: 19px;
      cursor: pointer;
      margin-right: 0;
      border-right: 1px solid rgba(255, 255, 255, 0.45);
      border-radius: 0;
      font-weight: 600;
      transition: background-color 0.2s;

      &:last-child {
        border-right: none;
      }

      &.active {
        background: #2f6ec6;
        font-weight: 700;
      }

      &:hover:not(.active) {
        background: #7d858f;
      }
    }
  }

  .training-section {
    margin-top: 0;
    background: #fff;
    border: 1px solid #e8edf5;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(18, 32, 56, 0.04);
    padding-top: 14px;
    padding-bottom: 8px;
    padding-left: 0;
    padding-right: 0;

    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      padding: 0 16px;

      .icon-square {
        width: 16px;
        height: 16px;
        margin-right: 12px;
      }

      .title {
        color: #1d2129;
        font-size: 44px;
        font-weight: 700;
        transform: scale(0.45);
        transform-origin: left center;
        white-space: nowrap;
      }
    }
  }

  .training-section + .training-section {
    margin-top: 18px;
  }

  .status-tag {
    font-size: 14px;
    font-weight: 500;
  }

  .status-open {
    color: #5c5f66;
  }

  .status-not-start {
    color: #e6a23c;
  }

  .status-finished {
    color: #909399;
  }

  .status-unknown {
    color: #b0b5bd;
  }

  .enroll-btn {
    background-color: #035db3;
    border-color: #035db3;
    padding: 7px 16px;
    font-size: 14px;

    &:hover {
      opacity: 0.9;
    }
  }

  .enrolled-btn {
    background-color: #f0f2f5;
    border-color: #d9dde3;
    color: #8a9099;
    padding: 7px 16px;
    font-size: 14px;
  }

  .auditing-btn {
    background-color: #f0f2f5;
    border-color: #d9dde3;
    color: #8a9099;
    padding: 7px 16px;
    font-size: 14px;
  }

  .wait-pay-btn {
    background-color: #035db3;
    border-color: #035db3;
    padding: 7px 16px;
    font-size: 14px;
  }

  .detail-btn {
    color: #606266;
    &:hover {
      color: #035db3;
      border-color: #035db3;
      background: #ecf5ff;
    }
  }

  ::v-deep .el-table th,
  ::v-deep .el-table td {
    padding: 10px 0;
  }

  ::v-deep .el-table__empty-text {
    color: #9aa1a9;
  }
}
</style>
